import type {
  PaymentProvider,
  SubscriptionCurrency,
  SubscriptionOverride,
  SubscriptionPrice,
} from '@/types/api'

export interface EffectivePrice {
  /** Базовая цена (с учётом override.price, но без discount). Зачёркивается, если есть скидка. */
  basePrice: number
  baseCurrency: SubscriptionCurrency
  /** Цена со скидкой. Равна basePrice, если скидки нет. */
  finalPrice: number
  finalCurrency: SubscriptionCurrency
  /** Доля скидки [0..1]; null/undefined если скидки нет. */
  discount: number | null
  /** true, если скидка реально применилась (нужно зачёркивать basePrice). */
  hasDiscount: boolean
}

const parseDiscount = (raw?: string | null): number | null => {
  if (raw == null) return null
  const parsed = Number.parseFloat(raw)
  if (!Number.isFinite(parsed) || parsed <= 0) return null
  return Math.min(parsed, 1)
}

/**
 * Провайдер тарифа по-умолчанию определяется по `tribute_id`: есть → tribute,
 * нет → robokassa. Эта же эвристика используется в ProviderPickerModal.
 * Если на тариф пришёл override с provider — он перебивает эвристику
 * (override фактически переводит тариф под этого провайдера).
 */
export const planProvider = (
  plan: SubscriptionPrice,
  override?: SubscriptionOverride | null
): PaymentProvider => {
  if (
    override?.provider != null &&
    override.subscription_info_id === plan.subscription_info_id
  ) {
    return override.provider
  }
  return plan.tribute_id != null ? 'tribute' : 'robokassa'
}

/**
 * Применяет override к тарифу: сначала price/currency (если override указан
 * на этот subscription_info_id), затем discount. Скидка применяется ко всем
 * тарифам — один и тот же тариф доступен и через Tribute, и через Robokassa,
 * выбор провайдера происходит на следующем шаге. override.provider оставлен
 * для будущих сценариев, но фронт его сейчас не учитывает.
 */
export const applyOverride = (
  plan: SubscriptionPrice,
  override?: SubscriptionOverride | null
): EffectivePrice => {
  let basePrice = plan.price
  let baseCurrency: SubscriptionCurrency = plan.currency

  if (override && override.subscription_info_id === plan.subscription_info_id) {
    if (override.price != null) basePrice = override.price
    if (override.currency != null) baseCurrency = override.currency as SubscriptionCurrency
  }

  const discount = parseDiscount(override?.discount)

  if (discount == null) {
    return {
      basePrice,
      baseCurrency,
      finalPrice: basePrice,
      finalCurrency: baseCurrency,
      discount: null,
      hasDiscount: false,
    }
  }

  const finalPrice = Math.max(0, Math.round(basePrice * (1 - discount)))
  return {
    basePrice,
    baseCurrency,
    finalPrice,
    finalCurrency: baseCurrency,
    discount,
    hasDiscount: finalPrice !== basePrice,
  }
}
